import { MAIN_APP_API_URL, OWN_DELIVERY_TYPE } from "../utils/constants";

class MainAppApi {
  // constructor({ baseUrl }) {
  //   MAIN_URL = baseUrl;
  // }

  _checkResponse(res) {
    if (res.ok) {
      return res.json();
    } else {
      const statusCode = res.status;
      return res.json().then((res) => {
        return Promise.reject({
          statusCode: statusCode,
          message: res.msg,
          detail: res.detail,
        });
      });
    }
  }

  _checkResponseWithCookies({ res, func, params }) {
    if (res.ok) {
      return res.json();
    } else {
      const statusCode = res.status;

      return res.json().then((resBody) => {
        const detail = resBody.detail;

        if (statusCode === 401 && detail === "Signature has expired") {
          // Access token expired → try to refresh JWT
          return mainAppApi
            .refreshJWT()
            .then(() => func(params))
            .catch((err) => Promise.reject(err));
        }

        // Any other 401 or error – just reject
        return Promise.reject({
          statusCode,
          message: resBody.msg,
          detail: resBody.detail,
        });
      });
    }
  }

  refreshJWT() {
    const doRefresh = () => {
      return fetch(`${MAIN_APP_API_URL}/auth/refresh-jwt?webview=1`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        }

        const statusCode = res.status;
        let resBody = {};
        try {
          resBody = await res.json();
        } catch (e) {
          resBody = {};
        }

        // Only refreshJWT can get this error
        if (statusCode === 401 && resBody.detail === "JTI parent access expired") {
          // Tell native app to refresh parent token
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              message: "401 JTI parent access expired",
            })
          );

          // Wait for native app confirmation, then try refresh again once
          return new Promise((resolve, reject) => {
            const handler = (event) => {
              try {
                const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
                const message = data?.message;

                if (message === "401 JTI parent access refreshed") {
                  window.removeEventListener("message", handler);

                  // Now that parent token is refreshed, try refreshJWT once more
                  doRefresh()
                    .then(resolve)
                    .catch(reject);
                }
              } catch (err) {
                // Ignore malformed messages, wait for the correct one
              }
            };

            window.addEventListener("message", handler);

            // Optional timeout
            setTimeout(() => {
              window.removeEventListener("message", handler);
              reject({
                statusCode,
                message: "Timeout waiting for JWT refresh from app",
              });
            }, 10000); // 10 seconds
          });
        }

        // Any other error from refresh endpoint
        return Promise.reject({
          statusCode,
          message: resBody.msg,
          detail: resBody.detail,
        });
      });
    };

    return doRefresh();
  }


  getMe() {
    return fetch(
      `${MAIN_APP_API_URL}/auth/get-me`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getMe,
        params: {},
      })
    );
  }

  editPersonalData({ name, avatar, is_notifications_enabled }) {
    return fetch(
      `${MAIN_APP_API_URL}/auth/register/edit-personal-data`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name, avatar, is_notifications_enabled,
        }),
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.editPersonalData,
        params: { name, avatar, is_notifications_enabled },
      })
    );
  }

  getUserAccessCode() {
    return fetch(
      `${MAIN_APP_API_URL}/auth/access-codes/get-user-code`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getUserAccessCode,
        params: {},
      })
    );
  }

  getAllNotifications() {
    return fetch(
      `${MAIN_APP_API_URL}/notifications/get-all`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getAllNotifications,
        params: {},
      })
    );
  }

  changeEmail({ email }) {
    return fetch(
      `${MAIN_APP_API_URL}/auth/change-email/main`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email
        }),
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.changeEmail,
        params: { email },
      })
    );
  }

  changeEmailCheckCode({ code }) {
    return fetch(
      `${MAIN_APP_API_URL}/auth/change-email/check-code`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code
        }),
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.changeEmailCheckCode,
        params: { code },
      })
    );
  }

  changePasswordCheckCurrent({ current_password }) {
    return fetch(
      `${MAIN_APP_API_URL}/auth/password/check`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password
        }),
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.changePasswordCheckCurrent,
        params: { current_password },
      })
    );
  }

  changePasswordUpdate({ current_password, new_password }) {
    return fetch(
      `${MAIN_APP_API_URL}/auth/password/update`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password, new_password
        }),
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.changePasswordUpdate,
        params: { current_password, new_password },
      })
    );
  }


  getOrders({ limit, last_id }) {
    let params = {};
    if (limit) params.limit = limit;
    if (last_id) params.last_id = last_id;
    return fetch(
      `${MAIN_APP_API_URL}/orders/get-all?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getOrders,
        params: { limit, last_id },
      })
    );
  }

  getExactOrder({ _id }) {
    let params = {};
    if (_id) params._id = _id;
    return fetch(
      `${MAIN_APP_API_URL}/orders/get-exact?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getExactOrder,
        params: { _id },
      })
    );
  }


  createOrder({
    shop_id,
    email,
    phone,
    data,
    first_name,
    last_name,
    submit_type,
    payment_type,
    promo_code,
    return_url,
    delivery_type,
    delivery_data,
    cart_uid,
    client_uid,
    coins_to_spend,
  }) {
    return fetch(`${MAIN_APP_API_URL}/orders/create`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop_id,
        email: email,
        phone: phone,
        data: data,
        first_name: first_name,
        last_name: last_name,
        submit_type: submit_type,
        delivery_data: delivery_data && delivery_type === OWN_DELIVERY_TYPE ? delivery_data : null,
        delivery_type: delivery_type ? delivery_type : null,
        payment_type: payment_type,
        promo_code: promo_code,
        return_url: return_url ? return_url : null,
        client_uid: client_uid,
        cart_uid: cart_uid,
        coins_to_spend: coins_to_spend,
      }),
    }).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.createOrder,
        params: {
          shop_id,
          email,
          phone,
          data,
          first_name,
          last_name,
          submit_type,
          payment_type,
          promo_code,
          return_url,
          delivery_type,
          delivery_data,
          cart_uid,
          client_uid,
          coins_to_spend,
        },
      })
    );
  }

  calculateOrder({
    shop_id,
    data,
    promo_code,
    delivery_type,
    delivery_data,
  }) {
    return fetch(`${MAIN_APP_API_URL}/orders/calculate-order`, {
      method: "POST",
      // credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop_id,
        data: data,
        promo_code: promo_code,
        delivery_type: delivery_type ? delivery_type : null,
        delivery_data: delivery_data ? delivery_data : null,
      }),
    }).then(this._checkResponse);
  }


  getCollectionItems({ limit, last_id }) {
    let params = {};
    if (limit) params.limit = limit;
    if (last_id) params.last_id = last_id;
    return fetch(
      `${MAIN_APP_API_URL}/collection/get-all?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getCollectionItems,
        params: { limit, last_id },
      })
    );
  }

  getCollectionMainStats({ start, end }) {
    let params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return fetch(
      `${MAIN_APP_API_URL}/collection/main-stats?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getCollectionMainStats,
        params: { start, end },
      })
    );
  }

  collectionCreateItem({ name, reference, image, purchase_price, purchase_date }) {
    return fetch(
      `${MAIN_APP_API_URL}/collection/create`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name, reference, image, purchase_price, purchase_date
        }),
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.collectionCreateItem,
        params: { name, reference, image, purchase_price, purchase_date },
      })
    );
  }

  getCollectionItemStats({ _id, start, end }) {
    let params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    if (_id) params._id = _id;
    params.period = '1day'

    return fetch(
      `${MAIN_APP_API_URL}/collection/item-stats?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getCollectionItemStats,
        params: { _id, start, end },
      })
    );
  }

  getCollectionItem({ _id }) {
    let params = {};
    if (_id) params._id = _id;

    return fetch(
      `${MAIN_APP_API_URL}/collection/get-exact?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.getCollectionItem,
        params: { _id },
      })
    );
  }

  collectionDeleteItem({ _id }) {
    let params = {};
    if (_id) params._id = _id;
    return fetch(
      `${MAIN_APP_API_URL}/collection/delete?` +
      new URLSearchParams({ data: JSON.stringify(params) }),
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.collectionDeleteItem,
        params: { _id },
      })
    );
  }





  deleteAccount() {
    return fetch(
      `${MAIN_APP_API_URL}/auth/delete`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res,
        func: mainAppApi.deleteAccount,
        params: {},
      })
    );
  }


  async uploadImage({ data, type }) {
    return fetch(
      `${MAIN_APP_API_URL}/images/upload?` +
      new URLSearchParams({ data: JSON.stringify({ type }) }),
      {
        method: "POST",
        credentials: "include",
        body: data,
      }
    ).then((res) =>
      mainAppApi._checkResponseWithCookies({
        res: res,
        func: mainAppApi.uploadImage,
        params: { data, type },
      })
    );
  }

}



const mainAppApi = new MainAppApi({
  baseUrl: MAIN_APP_API_URL,
});

export default mainAppApi;
